// © Microsoft Corporation. All rights reserved.

import { ExperienceMilestoneEventTypes, MilestonePlugin } from "./data-model";

import { ActionMap } from "@msnews/experiences-redux";
import { ExperienceDeferralInfo } from "./data-model/ExperienceDeferralInfo";
import { ExperienceLoadingState } from "./data-model/ExperienceLoadingState";

/**
 * Base arguments for all experience milestone updates
 */
export interface UpdateArgsBase {
    /** Experience Type - Defines an experience */
    experienceType: string;

    /** Associated Experience Group */
    experienceGroup?: string;

    /** Defines an instance of the Experience */
    experienceInstance?: string;
}

export interface UpdateExperiencePublicStateArgs extends UpdateArgsBase {
    /** Current Milestone eventType of the experience e.g loaded, staleRevalidationComplete etc */
    eventType: ExperienceMilestoneEventTypes;
}

export interface UpdateExperienceCurrentStateArgs extends UpdateArgsBase {
    /** Current Milestone of the experience e.g initialized, loaded, deferred etc */
    currentState: ExperienceLoadingState;
}

export interface UpdateExperienceDeferralArgs extends UpdateArgsBase {
    /** Tracks deferral information used by an experience to determine when it should load/render */
    deferralInfo: ExperienceDeferralInfo;
}

export interface UpdateTimingArgs extends UpdateArgsBase {
    /** start time fo the milestone e.g. startTime of loaded milestone */
    startTime?: number;

    /** end time fo the milestone e.g. endTime of loaded milestone */
    endTime?: number;
}

export type UpdateExperienceCurrentStateSignature = (info: Array<UpdateExperienceCurrentStateArgs>) => void;

export type UpdateExperiencePublicStateSignature = (args: UpdateExperiencePublicStateArgs) => void;

export type UpdateExperienceDeferralSignature = (info: Array<UpdateExperienceDeferralArgs>) => void;

export type UpdateTimingSignature = (args: UpdateTimingArgs) => void;

export type UpdatePluginSignature = (
    /** Plugins to add additional behaviors in page lifecycle events */
    plugins: MilestonePlugin[]
) => void;

export type UpdatePageTimingSignature = (time?: number) => void;

export type UpdatePageRevealStartSignature = (startTimeMs: number, wasHidden: boolean, visuallyReadyDependencies: string[]) => void;

export type UpdateBelowTheFoldTransitionSignature = (start?: boolean) => void;

type UpdateHydrationCompleteSignature = () => void;

type UpdateRiverHydrationCompleteSignature = () => void;

/**
 * Actions for the Milestone Data Connector
 * @class
 */
export class MilestoneDataActionsImpl {

    /** Action to trigger hydration timing updates */
    public updateHydrationComplete: ActionMap<UpdateHydrationCompleteSignature>
    = new ActionMap<UpdateHydrationCompleteSignature>("updateHydrationRenderComplete");

    /** Action to trigger River hydration timing updates */
    public updateRiverHydrationComplete: ActionMap<UpdateRiverHydrationCompleteSignature>
    = new ActionMap<UpdateRiverHydrationCompleteSignature>("updateRiverHydrationRenderComplete");

    /** Action to trigger above the fold timing updates */
    public updateAboveTheFoldVisuallyReady: ActionMap<UpdatePageTimingSignature>
    = new ActionMap<UpdatePageTimingSignature>("updateAboveTheFoldVisuallyReady");

    /** Action to trigger below the fold transition updates */
    public updateBelowTheFoldTransitionStart: ActionMap<UpdateBelowTheFoldTransitionSignature>
    = new ActionMap<UpdateBelowTheFoldTransitionSignature>("updateBelowTheFoldTransitionStart");

    /** Action to trigger when deferring an experience load */
    public updateExperienceLoadDeferralState: ActionMap<UpdateExperienceDeferralSignature>
    = new ActionMap<UpdateExperienceDeferralSignature>("updateExperienceLoadDeferralState");

    /** Action to trigger when updating current state of experience in load/render life-cycle */
    public updateExperienceCurrentState: ActionMap<UpdateExperienceCurrentStateSignature>
    = new ActionMap<UpdateExperienceCurrentStateSignature>("updateExperienceCurrentState");

    /** Action to trigger when updating current state of experience through publicly exposed MilestoneExperienceEventTypes */
    public updateExperiencePublicState: ActionMap<UpdateExperiencePublicStateSignature>
    = new ActionMap<UpdateExperiencePublicStateSignature>("updateExperiencePublicState");

    /** Action to trigger when deferring an experience render */
    public updateExperienceRenderDeferralState: ActionMap<UpdateExperienceDeferralSignature>
    = new ActionMap<UpdateExperienceDeferralSignature>("updateExperienceRenderDeferralState");

    /** Action to trigger when experience load initiates */
    public updateInitTiming: ActionMap<UpdateTimingSignature>
    = new ActionMap<UpdateTimingSignature>("UpdateInitTiming");

    /** Action to trigger page complete timing updates */
    public updatePageComplete: ActionMap<UpdatePageTimingSignature>
    = new ActionMap<UpdatePageTimingSignature>("updatePageCompleteTiming");

    /** Action to trigger page complete timing updates */
    public updatePageRevealStart: ActionMap<UpdatePageRevealStartSignature>
    = new ActionMap<UpdatePageRevealStartSignature>("updatePageRevealStart");

    /** Action to trigger page complete timing updates */
    public updatePageRevealComplete: ActionMap<UpdatePageTimingSignature>
    = new ActionMap<UpdatePageTimingSignature>("updatePageRevealComplete");

    /** Action to trigger when experience instance completed render */
    public updateRenderTiming: ActionMap<UpdateTimingSignature>
    = new ActionMap<UpdateTimingSignature>("UpdateRenderTiming");

    /** Action to trigger when experience instance is visually ready */
    public updateVisuallyReadyTiming: ActionMap<UpdateTimingSignature>
    = new ActionMap<UpdateTimingSignature>("UpdateVisuallyReadyTiming");

    /** Action to trigger when experience instance starts loading */
    public updateStartLoadingTiming: ActionMap<UpdateTimingSignature>
    = new ActionMap<UpdateTimingSignature>("UpdateStartLoadingTiming");

    /** Action to trigger when experience instance is DOM complete */
    public updateDomCompleteTiming: ActionMap<UpdateTimingSignature>
    = new ActionMap<UpdateTimingSignature>("UpdateDomCompleteTiming");

    /** Action to trigger when adding/updating milestone plugin */
    public updateMilestonePlugins: ActionMap<UpdatePluginSignature>
    = new ActionMap<UpdatePluginSignature>("updateMilestonePlugin");

    /** Reset the static instance. Useful when server side rendering. */
    public resetInstance(): void {

        this.updateHydrationComplete.unregisterObservers();
        this.updateRiverHydrationComplete.unregisterObservers();
        this.updateAboveTheFoldVisuallyReady.unregisterObservers();
        this.updateBelowTheFoldTransitionStart.unregisterObservers();
        this.updateExperienceLoadDeferralState.unregisterObservers();
        this.updateExperienceCurrentState.unregisterObservers();
        this.updateExperiencePublicState.unregisterObservers();
        this.updateExperienceRenderDeferralState.unregisterObservers();
        this.updateInitTiming.unregisterObservers();
        this.updatePageComplete.unregisterObservers();
        this.updatePageRevealStart.unregisterObservers();
        this.updatePageRevealComplete.unregisterObservers();
        this.updateRenderTiming.unregisterObservers();
        this.updateVisuallyReadyTiming.unregisterObservers();
        this.updateDomCompleteTiming.unregisterObservers();
        this.updateMilestonePlugins.unregisterObservers();

        MilestoneDataActions = new MilestoneDataActionsImpl();
    }
}

/** The singleton instance */
export let MilestoneDataActions = new MilestoneDataActionsImpl();